import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Form } from "antd";
import BasicTab from "./tabs/edit/BasicTab";
import ConfigTab from "./tabs/edit/ConfigTab";
import ComboSchedulePage from "./ComboSchedulePage";
import configFile from 'config.json';
import { NotificationManager } from 'react-notifications';
import axios from 'utils/axios';
import { useNavigate, useParams } from 'react-router-dom';

const tabItems = [
    { key: "basic", label: "Үндсэн мэдээлэл" },
    { key: "config", label: "Багцын тохиргоо" },
    { key: "schedule", label: "Цагийн тохиргоо"}
];

const API_BASE = `${configFile.PROD_SERVER_URL}`
const EditComboPage = () => {
    const [activeTab, setActiveTab] = useState("basic");
    const [deal, setDeal] = useState({})
    const [isLoading, setIsLoading] = useState(false);
    const { comboId } = useParams()
    const [basicForm] = Form.useForm();
    const [tags, setTags] = useState([])
    const [availableImages, setAvailableImages] = useState([])
    const navigate = useNavigate();

    const fetchCombo = useCallback(async () => {
        try {
            setIsLoading(true);
            const { data: result } = await axios.post(`${API_BASE}/menu2/combo/getCombo`, { comboId })
            setDeal(result.data)
        } catch (err) {
            console.log(err)
            NotificationManager.error(
                err?.response?.data?.errorMessage || 'Сервертэй холбогдоход алдаа гарлаа',
                "Error",
                5000
            );
        } finally {
            setIsLoading(false);
        }
    }, [comboId]);

    const fetchTags = useCallback(async () => {
        try {
            const { data: result } = await axios.post(`${API_BASE}/tag/getAllTags`)
            setTags(result.data)
        } catch (err) {
            console.log(err)
            NotificationManager.error(
                err?.response?.data?.errorMessage || 'Сервертэй холбогдоход алдаа гарлаа',
                "Error",
                5000
            );
        }
    }, []);

    const fetchImages = useCallback(async () => {
        try {
            const { data: result } = await axios.post(`${API_BASE}/menu2/image/list`);
            setAvailableImages(result?.data || []);
        } catch (err) {
            console.log(err);
        }
    }, []);

    useEffect(() => {
        fetchTags()
        fetchCombo()
        fetchImages()
    }, [fetchCombo, fetchTags, fetchImages]);
    return (
        <Page>
            <HeaderCard>
                <HeaderLeft>
                    <Breadcrumb>Комбо / {deal.masterName} засварлах</Breadcrumb>
                </HeaderLeft>
                <HeaderActions>
                    <Button onClick={() => navigate('/deals')}>Буцах</Button>
                </HeaderActions>
            </HeaderCard>

            <TabsBar>
                {tabItems.map((tab) => (
                    <TabButton
                        key={tab.key}
                        type="button"
                        $active={activeTab === tab.key}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </TabButton>
                ))}
            </TabsBar>

            <ContentWrap>
                {isLoading && <LoadingCard>Ачаалж байна...</LoadingCard>}
                {!isLoading && activeTab === "basic" && (
                    <BasicTab
                        key={deal.id || deal.comboMenuId || deal.masterName}
                        deal={deal}
                        form={basicForm}
                        tagOptions={tags}
                        onSaved={fetchCombo}
                        availableImages={availableImages}
                    />
                )}
                {!isLoading && activeTab === "config" && (
                    <ConfigTab deal={deal} tagOptions={tags} />
                )}
                {!isLoading && activeTab==="schedule" && (
                    <ComboSchedulePage comboId={deal?.id || comboId} />
                )}
            </ContentWrap>
        </Page>
    );
};

const Page = styled.div`
  --ink-900: #1f2a33;
  --ink-700: #3b4b5a;
  --ink-500: #6b7a88;
  --blue-700: #0958d9;
  --blue-600: #1677ff;
  --blue-500: #4096ff;
  --blue-200: #bae0ff;
  --card-bg: rgba(255, 255, 255, 0.94);
  --card-border: rgba(22, 119, 255, 0.18);
  --shadow-soft: 0 18px 50px rgba(33, 49, 63, 0.1);
  --shadow-float: 0 16px 30px rgba(22, 119, 255, 0.2);
  --radius-lg: 8px;
  --radius-md: 6px;
  --radius-sm: 5px;

  padding: 12px 16px 16px;
  background: radial-gradient(circle at 15% 15%, rgba(22, 119, 255, 0.2), transparent 45%),
    radial-gradient(circle at 85% 25%, rgba(186, 224, 255, 0.45), transparent 55%),
    linear-gradient(140deg, #f5f8ff 0%, #eef3fb 55%, #f6f7fb 100%);
  min-height: 100vh;
  color: var(--ink-900);
  font-family: "Sora", "Manrope", "SF Pro Display", sans-serif;
  letter-spacing: 0.01em;

  * {
    box-sizing: border-box;
  }

  .ant-btn,
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-picker,
  .ant-modal-content,
  .ant-modal-header,
  .ant-modal-footer,
  .ant-radio-button-wrapper,
  .ant-tag {
    border-radius: var(--radius-sm) !important;
  }
`;

const HeaderCard = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 22px;
  margin-bottom: 16px;
  border-radius: var(--radius-lg);
  background: var(--card-bg);
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--card-border);
  backdrop-filter: blur(8px);
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Breadcrumb = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 12px;
  color: var(--ink-500);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;

  .ant-btn-primary {
    background: linear-gradient(135deg, var(--blue-600), var(--blue-500));
    border: none;
    box-shadow: var(--shadow-float);
  }
`;

const TabsBar = styled.div`
  display: flex;
  gap: 24px;
  padding: 8px 2px 12px;
  border-bottom: 1px solid #e6e9ee;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  border: none;
  background: transparent;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? "var(--blue-600)" : "var(--ink-500)")};
  border-bottom: 2px solid
    ${({ $active }) => ($active ? "var(--blue-600)" : "transparent")};
  cursor: pointer;
  transition: color 0.2s ease;
`;

const ContentWrap = styled.div`
  display: grid;
  gap: 16px;
`;

const LoadingCard = styled.div`
  padding: 18px 20px;
  border-radius: 8px;
  border: 1px solid rgba(22, 119, 255, 0.16);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 12px 28px rgba(22, 119, 255, 0.08);
  color: var(--ink-700);
  font-weight: 600;
`;

export default EditComboPage;
